import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SearchInputComponent } from '../../../../components/inputs/search-input/search-input.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../../../components/inputs/status-filter/status-filter.component';
import { PaginationComponent } from '../../../../components/inputs/pagination/pagination.component';
import { PromptCardComponent } from '../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { StatusComponent } from '../../../../components/ui/status/status.component';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { MessageReportTableComponent } from './message-report-table/message-report-table.component';
import { DeliverySummaryComponent } from './delivery-summary/delivery-summary.component';
import { DeliveryRateByRecipientsGraphComponent } from '../../../../components/organization/campaigns/report-graphs/delivery-rate-by-recipients-graph/delivery-rate-by-recipients-graph.component';
import { GenderDistributionGraphComponent } from '../../../../components/organization/campaigns/report-graphs/gender-distribution-graph/gender-distribution-graph.component';
import { AgeGroupDistributionGraphComponent } from '../../../../components/organization/campaigns/report-graphs/age-group-distribution-graph/age-group-distribution-graph.component';
import { MessageService } from '../../../../services/message/message.service';
import { DeliveryService, DeliverySummary } from '../../../../services/message/delivery.service';
import {
  IMessageReportContacts,
  ReportChartData,
} from '../../campaigns/types/messagesTypes';
import { formatCount, formatWhen, sendMoment, smsParts } from '../../../../../utils/sms';
import { csvFileName, downloadCsv, toCsv } from '../../../../../utils/csv';
import {
  COLLECT_CAP,
  DeliveryFilter,
  RECEIPT_CSV_COLUMNS,
  ResendTarget,
  collectReportRows,
  deliveryCriteria,
  exactTime,
  isDeliveryFilter,
  openComposerForResend,
  resendNumbers,
  resendTarget,
  summaryCount,
} from './report-shared';

export type TArrangementSort =
  | 'fullName,asc'
  | 'fullName,desc'
  | 'createdAt,desc'
  | 'createdAt,asc'
  | 'modifiedAt,desc'
  | 'modifiedAt,asc';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const FILTER_NAMES: Record<DeliveryFilter, string> = {
  all: 'recipients',
  delivered: 'delivered',
  failed: 'failed',
  inflight: 'unconfirmed',
};

@Component({
  selector: 'app-message-reports',
  standalone: true,
  imports: [
    RouterLink,
    MatTooltipModule,
    SearchInputComponent,
    SortDropdownComponent,
    StatusFilterComponent,
    PaginationComponent,
    PromptCardComponent,
    StatusComponent,
    HasPermissionDirective,
    MessageReportTableComponent,
    DeliverySummaryComponent,
    DeliveryRateByRecipientsGraphComponent,
    GenderDistributionGraphComponent,
    AgeGroupDistributionGraphComponent,
  ],
  templateUrl: './message-reports.component.html',
  styleUrl: './message-reports.component.css',
})
export class MessageReportsComponent implements OnInit {
  /** Message uuid, bound from the route (withComponentInputBinding). */
  @Input() id!: string;

  @ViewChild(SearchInputComponent) private searchInput?: SearchInputComponent;

  messageService = inject(MessageService);
  private delivery = inject(DeliveryService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  readonly formatCount = formatCount;
  readonly collectCap = COLLECT_CAP;

  // ── Message header ──
  messageDetails: Record<string, any> = {};
  messageState: LoadState = 'loading';

  // ── Delivery summary ──
  summary: DeliverySummary | null = null;
  summaryState: LoadState = 'idle';
  blacklisted: number | null = null;

  // ── Receipts ──
  params: Record<string, unknown> = {};
  searchTerm: string = '';
  arrangementSort: TArrangementSort = 'createdAt,desc';
  filter: DeliveryFilter = 'all';
  page = 0;
  pageSize = 20;
  loading: boolean = false;
  loadError = '';
  messageReport: IMessageReportContacts[] = [];
  /** From X-Total-Count; null when the API did not say. */
  reportTotal: number | null = null;
  private load$ = new Subject<void>();

  // ── Charts ──
  loadingCharts: boolean = false;
  chartsError = false;
  messageCharts: ReportChartData | null = null;

  // ── Export and resend ──
  exportBusy = false;
  exportNote = '';
  exportError = '';
  resendBusy = false;
  resendError = '';
  resendPrompt: ResendTarget | null = null;
  /** Export and resend run separately, so starting one never cancels the other. */
  private exportWork?: Subscription;
  private resendWork?: Subscription;

  readonly filterOptions: StatusFilterOption[] = [
    { value: 'all', title: 'All recipients', icon: 'list', tone: 'neutral' },
    { value: 'delivered', title: 'Delivered', icon: 'check_circle', tone: 'success' },
    { value: 'failed', title: 'Failed', icon: 'error', tone: 'danger' },
    // Neutral, like the "still in flight" part of the delivery bar above the list.
    { value: 'inflight', title: 'No receipt yet', icon: 'schedule', tone: 'neutral' },
  ];

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'fullName',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.exportWork?.unsubscribe();
      this.resendWork?.unsubscribe();
    });
  }

  // ── Derived values for the template ──

  get uuid(): string {
    return this.id || String(this.messageDetails['uuid'] ?? '');
  }

  get subject(): string {
    return String(this.messageDetails['messageSubject'] ?? '').trim() || 'Untitled message';
  }

  /** The H1: the subject, unless the message itself could not be read. */
  get pageTitle(): string {
    return this.messageState === 'error' ? 'Message details unavailable' : this.subject;
  }

  get status(): string {
    return String(this.messageDetails['status'] ?? '');
  }

  get sentAt(): Date | null {
    return sendMoment(this.messageDetails);
  }

  get whenLabel(): string {
    const status = this.status.toLowerCase();
    if (status === 'pending') return 'Scheduled for';
    if (status === 'completed' || status === 'active') return 'Sent';
    return 'Created';
  }

  get whenText(): string {
    const moment = this.sentAt;
    return moment ? formatWhen(moment) : '';
  }

  get whenExact(): string {
    return exactTime(this.sentAt);
  }

  get from(): string {
    return String(this.messageDetails['from'] ?? '').trim();
  }

  get partsText(): string {
    const content = String(this.messageDetails['content'] ?? '');
    if (!content) return '';
    const { parts } = smsParts(content);
    return parts === 1 ? '1 part' : `${parts} parts`;
  }

  get listTitle(): string {
    switch (this.filter) {
      case 'delivered':
        return 'Delivered recipients';
      case 'failed':
        return 'Failed recipients';
      case 'inflight':
        return 'Recipients with no receipt yet';
      default:
        return 'Recipients';
    }
  }

  get resendCount(): number {
    return this.resendPrompt?.numbers.length ?? 0;
  }

  get resendViaState(): boolean {
    return !!this.resendPrompt?.viaState;
  }

  get resendCapped(): boolean {
    return !!this.resendPrompt?.capped;
  }

  get resendBlacklisted(): number {
    return this.resendPrompt?.blacklisted ?? 0;
  }

  get failedCount(): number {
    return this.summary?.failed ?? 0;
  }

  get totalPages(): number {
    const total = this.knownTotal;
    if (total !== null) return Math.max(1, Math.ceil(total / this.pageSize));
    // No total: offer one more page while pages come back full.
    return this.messageReport.length === this.pageSize ? this.page + 2 : this.page + 1;
  }

  /** The list's size: X-Total-Count, else the summary count for this filter. */
  get knownTotal(): number | null {
    if (this.reportTotal !== null) return this.reportTotal;
    if (this.searchTerm.trim()) return null;
    return summaryCount(this.summary, this.filter);
  }

  get exportLabel(): string {
    if (this.searchTerm.trim()) return 'Export these results';
    switch (this.filter) {
      case 'delivered':
        return 'Export delivered';
      case 'failed':
        return 'Export failed';
      case 'inflight':
        return 'Export no receipt yet';
      default:
        return 'Export recipients';
    }
  }

  get emptyMessage(): string {
    if (this.loadError) return 'The recipient list could not be loaded.';
    switch (this.filter) {
      case 'failed':
        return 'Nothing failed.';
      case 'delivered':
        return 'No delivered receipts yet.';
      case 'inflight':
        return 'Every recipient has a receipt.';
      default:
        return 'No delivery receipts yet.';
    }
  }

  /** Read out after each load, so a filter, search or page change is announced. */
  get resultsAnnouncement(): string {
    if (this.loadError) return this.loadError;
    const noun = this.filter === 'all' ? 'recipients' : `${FILTER_NAMES[this.filter]} recipients`;
    const term = this.searchTerm.trim();
    const matching = term ? ` matching “${term}”` : '';
    const shown = this.messageReport.length;
    if (!shown) return `No ${noun}${matching}.`;
    const total = this.knownTotal;
    if (total === null) return `Showing ${formatCount(shown)} ${noun}${matching}.`;
    const page = this.totalPages > 1 ? `, page ${this.page + 1} of ${this.totalPages}` : '';
    return `Showing ${formatCount(shown)} of ${formatCount(total)} ${noun}${matching}${page}.`;
  }

  get emptyHint(): string {
    if (this.loadError) return 'Check your connection, then refresh the page to try again.';
    if (this.filter === 'all') return 'Receipts usually start arriving a few minutes after a message goes out.';
    return '';
  }

  // ── Receipts: search, filter, sort, paging ──

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    delete this.params['fullName.contains'];
    delete this.params['mobileNumber.contains'];
    const value = term.trim();
    if (value) {
      // Digits (with +, spaces or dashes) search the number; anything else the name.
      if (/^[+\d\s()-]+$/.test(value)) {
        this.params['mobileNumber.contains'] = value.replace(/[\s()-]/g, '');
      } else {
        this.params['fullName.contains'] = value;
      }
    }
    this.page = 0;
    this.load$.next();
  }

  // sorting to handle arrangement of table data
  updateSortArrangementCriteria(criteria: TArrangementSort) {
    this.arrangementSort = criteria;
    this.page = 0;
    this.load$.next();
  }

  updateFilter(value: string | null) {
    this.filter = isDeliveryFilter(value) ? value : 'all';
    this.page = 0;
    this.load$.next();
  }

  /** A summary tile filters the list; picking the active tile again shows everyone. */
  pickTile(filter: DeliveryFilter) {
    this.updateFilter(this.filter === filter && filter !== 'all' ? 'all' : filter);
  }

  clearFilters() {
    this.filter = 'all';
    this.page = 0;
    if (this.searchTerm && this.searchInput) {
      // Clearing the field emits '' at once, which reloads the list.
      this.searchInput.clear();
      return;
    }
    this.updateSearchTerm('');
  }

  goToPage(page: number) {
    const last = this.totalPages - 1;
    const next = Math.min(Math.max(0, page), Math.max(0, last));
    if (next === this.page && !this.loadError) return;
    this.page = next;
    this.load$.next();
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.page = 0;
    // app-pagination then emits changePage(0), which goToPage ignores as the current page.
    this.load$.next();
  }

  get updatedParams() {
    return {
      ...this.params,
      ...deliveryCriteria(this.filter),
      page: this.page,
      size: this.pageSize,
      // id breaks ties so rows never repeat or vanish between pages.
      sort: [this.arrangementSort, 'id,desc'],
    };
  }

  // ── Export and resend ──

  exportRows() {
    if (this.exportBusy || !this.uuid) return;
    this.exportBusy = true;
    this.exportError = '';
    this.exportNote = 'Preparing the file…';
    const criteria: Record<string, unknown> = { ...this.params, ...deliveryCriteria(this.filter) };

    this.exportWork?.unsubscribe();
    this.exportWork = collectReportRows(this.messageService, this.uuid, criteria, (loaded, total) => {
      this.exportNote = total
        ? `Preparing ${formatCount(Math.min(loaded, total))} of ${formatCount(total)} rows…`
        : `Preparing ${formatCount(loaded)} rows…`;
    }).subscribe({
      next: ({ rows, total, totalKnown, capped }) => {
        this.exportBusy = false;
        if (!rows.length) {
          this.exportNote = '';
          this.exportError = 'There are no rows to export with this search and filter.';
          return;
        }
        const label = this.filter === 'all' ? 'recipients' : `${FILTER_NAMES[this.filter]} recipients`;
        downloadCsv(csvFileName(label, this.subject), toCsv(rows, RECEIPT_CSV_COLUMNS));
        const of = totalKnown ? ` of ${formatCount(total)}` : '';
        this.exportNote = capped
          ? `Downloaded the first ${formatCount(rows.length)}${of} rows. Narrow the search or filter to export the rest.`
          : `Downloaded ${formatCount(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}.`;
      },
      error: () => {
        this.exportBusy = false;
        this.exportNote = '';
        this.exportError = 'The export could not be prepared. Check your connection and try again.';
      },
    });
  }

  resendToFailed() {
    if (this.resendBusy || !this.uuid) return;
    this.resendBusy = true;
    this.resendError = '';

    this.resendWork?.unsubscribe();
    this.resendWork = collectReportRows(this.messageService, this.uuid, deliveryCriteria('failed')).subscribe({
      next: ({ rows, capped }) => {
        this.resendBusy = false;
        const { numbers, blacklisted } = resendNumbers(rows);
        if (!numbers.length) {
          this.resendError = blacklisted
            ? 'Every failed number is blacklisted, so there is no one to resend to.'
            : 'No failed numbers were found for this message.';
          return;
        }
        const target = resendTarget(numbers, String(this.messageDetails['messageSubject'] ?? ''), blacklisted, capped);
        if (target.viaState || target.capped) {
          this.resendPrompt = target;
          return;
        }
        openComposerForResend(this.router, target);
      },
      error: () => {
        this.resendBusy = false;
        this.resendError = 'The failed numbers could not be loaded. Check your connection and try again.';
      },
    });
  }

  confirmResend() {
    const target = this.resendPrompt;
    this.resendPrompt = null;
    if (target) openComposerForResend(this.router, target);
  }

  closeResendPrompt() {
    this.resendPrompt = null;
  }

  // ── Loading ──

  private loadMessage() {
    // Normal navigation hands the message over; use it only if it is this one.
    const cached = this.messageService.getSelectedMessageDetails as Record<string, any>;
    const cachedMatches = !!cached?.['messageSubject'] && cached['uuid'] === this.id;
    if (cachedMatches) {
      this.messageDetails = cached;
      this.messageState = 'ready';
    }

    this.messageService
      .getMessage(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (message) => {
          if (message && typeof message === 'object') {
            this.messageDetails = message;
          }
          this.messageState = 'ready';
        },
        error: () => {
          // Receipts are keyed by the uuid in the link, so the rest still loads.
          this.messageState = cachedMatches ? 'ready' : 'error';
        },
      });
  }

  private loadSummary() {
    if (!this.id) return;
    this.summaryState = 'loading';
    this.delivery
      .summary(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summary) => {
          this.summary = summary;
          this.summaryState = 'ready';
        },
        error: () => {
          this.summaryState = 'error';
        },
      });

    this.messageService
      .countMessageReports(this.id, { 'blackListed.equals': true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (count) => (this.blacklisted = count),
        error: () => (this.blacklisted = null),
      });
  }

  private loadCharts() {
    this.loadingCharts = true;
    this.chartsError = false;
    this.messageService
      .getMessageChartReport(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (report) => {
          this.messageCharts = report;
          this.loadingCharts = false;
        },
        error: () => {
          this.loadingCharts = false;
          this.chartsError = true;
        },
      });
  }

  ngOnInit() {
    // The dashboard and details page link here with ?status=failed and the like.
    const status = this.route.snapshot.queryParamMap.get('status');
    if (isDeliveryFilter(status)) this.filter = status;

    this.load$
      .pipe(
        switchMap(() => {
          this.loading = true;
          this.loadError = '';
          return this.messageService.fetchMessageReportPage(this.id, this.updatedParams).pipe(
            map((result) => ({ ...result, failed: false })),
            catchError(() => of({ rows: [] as any[], total: null as number | null, failed: true }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ rows, total, failed }) => {
        this.loading = false;
        this.messageReport = rows as IMessageReportContacts[];
        this.reportTotal = total;
        this.loadError = failed ? 'The recipient list could not be loaded.' : '';
      });

    this.loadMessage();
    this.loadSummary();
    this.load$.next();
    this.loadCharts();
  }
}
